import { useMemo, useState } from 'react'
import { Check, Search, X } from 'lucide-react'
import { useMoveLoadClasses } from '../../api/queries/moves'
import { MobileSearchPill } from '../search/MobileSearchPill'
import { SearchSheet } from '../search/SearchSheet'
import { SearchSection } from '../search/SearchSection'
import type { MovesSearch } from './MovesSearchContext'

/**
 * Airbnb-inspired "From / To / Load" search for Moves discovery — same pill +
 * mobile sheet pattern as thrifts. Controlled; the frame owns the state.
 */
export function MovesSearchBar({
  search,
  onChange,
}: {
  search: MovesSearch
  onChange: (next: MovesSearch) => void
}) {
  const { data: loadClasses } = useMoveLoadClasses()
  const loadOptions = useMemo(
    () => (loadClasses ?? []).map((c) => ({ key: c.key, label: c.label })),
    [loadClasses],
  )
  const loadLabel = useMemo(
    () => loadOptions.find((c) => c.key === search.loadClass)?.label ?? '',
    [loadOptions, search.loadClass],
  )
  const hasFilters =
    search.from.trim() !== '' || search.to.trim() !== '' || search.loadClass !== ''
  const summary = [search.from.trim(), search.to.trim(), loadLabel].filter(Boolean).join(' · ')

  const [sheetOpen, setSheetOpen] = useState(false)

  return (
    <>
      <div className="sm:hidden">
        <MobileSearchPill
          summary={summary}
          placeholder="Where are you moving?"
          onClick={() => setSheetOpen(true)}
          ariaLabel="Search moves"
        />
      </div>
      {sheetOpen && (
        <MovesSearchSheet
          search={search}
          loadOptions={loadOptions}
          onChange={onChange}
          onClose={() => setSheetOpen(false)}
        />
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
        }}
        className="mx-auto hidden w-full max-w-2xl flex-row items-center gap-1 rounded-full border border-stone-200 bg-white p-1.5 shadow-[0_3px_14px_rgba(28,25,23,0.10)] transition focus-within:border-brand-400 focus-within:shadow-[0_4px_20px_rgba(28,25,23,0.14)] sm:flex"
      >
        <label className="min-w-0 flex-1 cursor-text rounded-full px-4 py-2 transition hover:bg-stone-50">
          <span className="block text-[11px] font-bold uppercase tracking-wide text-stone-700">
            From
          </span>
          <input
            value={search.from}
            onChange={(e) => onChange({ ...search, from: e.target.value })}
            placeholder="Pickup area"
            aria-label="Pickup area"
            className="w-full bg-transparent text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none"
          />
        </label>

        <span className="h-8 w-px shrink-0 bg-stone-200" />

        <label className="min-w-0 flex-1 cursor-text rounded-full px-4 py-2 transition hover:bg-stone-50">
          <span className="block text-[11px] font-bold uppercase tracking-wide text-stone-700">
            To
          </span>
          <input
            value={search.to}
            onChange={(e) => onChange({ ...search, to: e.target.value })}
            placeholder="Drop-off area"
            aria-label="Drop-off area"
            className="w-full bg-transparent text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none"
          />
        </label>

        <span className="h-8 w-px shrink-0 bg-stone-200" />

        <label className="min-w-0 flex-[0.9] cursor-pointer rounded-full px-4 py-2 transition hover:bg-stone-50">
          <span className="block text-[11px] font-bold uppercase tracking-wide text-stone-700">
            Load
          </span>
          <select
            value={search.loadClass}
            onChange={(e) => onChange({ ...search, loadClass: e.target.value })}
            aria-label="Load type"
            className="w-full cursor-pointer bg-transparent text-sm text-stone-700 focus:outline-none"
          >
            <option value="">Any load</option>
            {loadOptions.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        {hasFilters ? (
          <button
            type="button"
            onClick={() => onChange({ from: '', to: '', loadClass: '' })}
            aria-label="Clear search"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-500 transition hover:bg-stone-50 hover:text-stone-700"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-700 text-white"
          >
            <Search className="h-5 w-5" />
          </span>
        )}
      </form>
    </>
  )
}

function MovesSearchSheet({
  search,
  loadOptions,
  onChange,
  onClose,
}: {
  search: MovesSearch
  loadOptions: { key: string; label: string }[]
  onChange: (next: MovesSearch) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<MovesSearch>(search)
  const [section, setSection] = useState<'from' | 'to' | 'load'>('from')
  const draftLoadLabel =
    loadOptions.find((c) => c.key === draft.loadClass)?.label ?? 'Any load'

  return (
    <SearchSheet
      open
      onClose={onClose}
      onClear={() => setDraft({ from: '', to: '', loadClass: '' })}
      onSubmit={() => {
        onChange(draft)
        onClose()
      }}
      title="Plan a move"
    >
      <SearchSection
        label="From"
        heading="Where from?"
        summary={draft.from.trim() || 'Pickup area'}
        expanded={section === 'from'}
        onExpand={() => setSection('from')}
      >
        <label className="flex items-center gap-3 rounded-xl border border-stone-300 px-4 py-3 focus-within:border-stone-900">
          <Search className="h-4 w-4 shrink-0 text-stone-500" />
          <input
            value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            placeholder="Village, town, or landmark"
            aria-label="Pickup area"
            className="w-full bg-transparent text-base text-stone-900 placeholder:text-stone-400 focus:outline-none"
          />
        </label>
      </SearchSection>

      <SearchSection
        label="To"
        heading="Where to?"
        summary={draft.to.trim() || 'Drop-off area'}
        expanded={section === 'to'}
        onExpand={() => setSection('to')}
      >
        <label className="flex items-center gap-3 rounded-xl border border-stone-300 px-4 py-3 focus-within:border-stone-900">
          <Search className="h-4 w-4 shrink-0 text-stone-500" />
          <input
            value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            placeholder="Village, town, or landmark"
            aria-label="Drop-off area"
            className="w-full bg-transparent text-base text-stone-900 placeholder:text-stone-400 focus:outline-none"
          />
        </label>
      </SearchSection>

      <SearchSection
        label="Load"
        heading="What are you moving?"
        summary={draftLoadLabel}
        expanded={section === 'load'}
        onExpand={() => setSection('load')}
      >
        <ul className="max-h-[46vh] space-y-1 overflow-y-auto">
          <LoadRow
            label="Any load"
            selected={draft.loadClass === ''}
            onSelect={() => setDraft((d) => ({ ...d, loadClass: '' }))}
          />
          {loadOptions.map((c) => (
            <LoadRow
              key={c.key}
              label={c.label}
              selected={draft.loadClass === c.key}
              onSelect={() => setDraft((d) => ({ ...d, loadClass: c.key }))}
            />
          ))}
        </ul>
      </SearchSection>
    </SearchSheet>
  )
}

function LoadRow({
  label,
  selected,
  onSelect,
}: {
  label: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-sm transition ${
          selected
            ? 'bg-stone-100 font-semibold text-stone-900'
            : 'text-stone-700 hover:bg-stone-50'
        }`}
      >
        <span className="truncate">{label}</span>
        {selected ? <Check className="h-4 w-4 shrink-0 text-brand-700" /> : null}
      </button>
    </li>
  )
}
